import { Component, CSSProperties, Children } from 'react'
import * as cn from 'classnames'
import SwipeableViews from 'react-swipeable-views'
import { mod } from 'react-swipeable-views-core'
import { virtualize } from 'react-swipeable-views-utils'
import * as range from 'lodash/range'
import * as styles from 'styles/molecules/Carousel.css'

const VirtualizeSwipeableViews = virtualize(SwipeableViews)

type CarouselOwnProps = {
  minSlideCount?: number
  order?: number[]
  isFinite?: boolean
  slideCount?: number
  slideStyle?: CSSProperties
  containerStyle?: CSSProperties
  firstSlideStyle?: CSSProperties
  className?: string
  activeIndex?: number
  refineIndex?: (nextActiveIndex: number, activeIndex: number, isSwipe: boolean) => number
  onIndexChange?: (activeIndex: number, realIndex: number) => void
}

type CarouselState = {
  activeIndex: CarouselOwnProps['activeIndex']
  currentActiveIndex: number
}

const MINIMAL_DOTS_NUMBER = 2

class Carousel extends Component<CarouselOwnProps, CarouselState> {
  static getDerivedStateFromProps (nextProps: CarouselOwnProps, prevState: CarouselState) {
    if (
      nextProps.activeIndex !== null
      && (
        nextProps.activeIndex !== prevState.activeIndex
        || nextProps.activeIndex !== prevState.currentActiveIndex
      )
    ) {
      return {
        currentActiveIndex: nextProps.activeIndex,
        activeIndex: nextProps.activeIndex,
      }
    }

    return {}
  }

  state = {
    activeIndex: this.props.activeIndex,
    currentActiveIndex: this.props.activeIndex || 0,
  }

  private get itemsCount () {
    return Children.count(this.props.children)
  }

  render () {
    const {
      className,
      children,
      minSlideCount = 1,
      slideCount,
      slideStyle,
      containerStyle,
      isFinite,
    } = this.props

    if (!Children.count(children)) {
      return null
    }

    const slides = Children.toArray(children)
    const slideRenderer = (slide: { key: string, index: number }) => {
      const slideWrapperClassName = cn(styles.slideWrapper, {
        [styles.invisible]: isFinite && slide.index >= this.itemsCount,
      })
      const slideIndex = mod(slide.index, this.itemsCount)

      return (
        <div key={ slide.key } className={ slideWrapperClassName }>
          { slides[slideIndex] }
        </div>
      )
    }

    return slides.length > minSlideCount ? (
      <>
        <VirtualizeSwipeableViews
          className={ cn(styles.carousel, className) }
          index={ this.state.currentActiveIndex }
          slideRenderer={ slideRenderer }
          onChangeIndex={ this.setCurrentActiveIndex.bind(this, true) }
          overscanSlideBefore={ this.itemsCount }
          overscanSlideAfter={ this.itemsCount }
          slideStyle={ slideStyle }
          containerStyle={ containerStyle }
          slideCount={ slideCount }
          threshold={ 10 }
        />
        { this.renderDots() }
      </>
    ) : (
      <div className={ cn(styles.carousel, className) }>
        <div style={ containerStyle } className={ styles.carouselInner }>
          { slides.map((_slide, s) => {
            const key = String(s)
            return (
              <div style={ slideStyle } className={ styles.carouselSlideWrpaper } key={ key }>
                { slideRenderer({ key, index: s }) }
              </div>
            )
          }) }
        </div>
      </div>
    )
  }

  renderDots = () => {
    if (this.itemsCount < MINIMAL_DOTS_NUMBER) {
      return null
    }

    const { currentActiveIndex } = this.state
    const { itemsCount } = this
    const indexes = range(itemsCount)

    return (
      <div className={ styles.dots }>
        { indexes.map((index) => {
          const nextActiveIndex = Math.floor(currentActiveIndex / itemsCount) * itemsCount + index
          return (
            <div
              key={ index }
              className={ cn(styles.dot, {
                [styles.activeDot]: index === this.getRealIndex(currentActiveIndex),
              }) }
              onClick={ this.setCurrentActiveIndex.bind(this, false, nextActiveIndex) }
            />
          )
        }) }
      </div>
    )
  }

  private setCurrentActiveIndex (isSwipe: boolean, currentActiveIndex: number) {
    if (this.props.refineIndex) {
      currentActiveIndex = this.props.refineIndex(currentActiveIndex, this.state.currentActiveIndex, isSwipe)
    }

    if (currentActiveIndex === this.state.currentActiveIndex) {
      return
    }

    this.setState({ currentActiveIndex })

    const { onIndexChange } = this.props
    if (onIndexChange) {
      // TODO fix slides being switched not pleasantly from the last to the first one
      const realIndex = this.getRealIndex(currentActiveIndex)
      onIndexChange(currentActiveIndex, realIndex)
    }
  }

  private getRealIndex (virtualIndex: number) {
    return mod(virtualIndex, this.itemsCount)
  }
}

export default Carousel
